import * as echarts from "echarts";
import React, { useEffect, useMemo, useRef } from "react";

import { ReasonMeta, RejectReasonByStage } from "@/types/job";

import { colorForKey } from "./utils/helpers";

type Props = {
  data: RejectReasonByStage;
};

const useReasonMeta = (data: RejectReasonByStage): ReasonMeta[] =>
  useMemo(() => {
    const allReasons = new Set<string>();
    Object.values(data).forEach((stage) => {
      Object.keys(stage).forEach((reason) => allReasons.add(reason));
    });

    return Array.from(allReasons).map((key) => ({
      key,
      label: key,
      color: colorForKey(key),
    }));
  }, [data]);

const RejectReasonChart: React.FC<Props> = ({ data }) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const chart = useRef<echarts.ECharts | null>(null);
  const reasons = useReasonMeta(data);
  const stages = useMemo(() => Object.keys(data), [data]);

  useEffect(() => {
    if (!ref.current || chart.current) return;
    chart.current = echarts.init(ref.current);
    return () => chart.current?.dispose();
  }, []);

  const option = useMemo<echarts.EChartsOption>(() => {
    if (!stages.length || !reasons.length) return {};

    return {
      animation: false,
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        formatter: (params: any) => {
          if (!Array.isArray(params)) return "";
          const stage = params[0]?.axisValue || "";
          let content = `<strong>${stage}</strong><br/>`;
          params.forEach((p: any) => {
            if (p.value > 0) {
              content += `${p.marker} ${p.seriesName}: ${p.value}<br/>`;
            }
          });
          return content;
        },
      },
      legend: {
        bottom: 0,
        left: "center",
        type: "scroll",
      },
      grid: { top: 20, left: 40, right: 20, bottom: 100, containLabel: true },
      xAxis: {
        type: "category",
        data: stages,
        axisLabel: {
          interval: 0,
          rotate: 0,
        },
      },
      yAxis: { type: "value" },
      series: reasons.map((reason) => ({
        name: reason.label,
        type: "bar",
        stack: "total",
        barWidth: 60,
        itemStyle: { color: reason.color },
        data: stages.map((stage) => data[stage][reason.key] ?? 0),
      })),
    };
  }, [data, stages, reasons]);

  useEffect(() => {
    if (stages.length && reasons.length) {
      chart.current?.setOption(option, true);
    } else {
      chart.current?.clear();
    }
  }, [option, stages.length, reasons.length]);

  useEffect(() => {
    if (!ref.current || !chart.current) return;
    const ro = new ResizeObserver(() => chart.current?.resize());
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);

  if (!stages.length || !reasons.length) {
    return (
      <div className="w-full py-20 text-center text-gray-500">
        No data available
      </div>
    );
  }

  return (
    <div className="w-full rounded p-6 shadow">
      <div className="w-full overflow-x-auto p-2">
        <div ref={ref} style={{ height: 400, minWidth: stages.length * 120 }} />
      </div>
    </div>
  );
};

export default RejectReasonChart;
